import { useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { invalidateQuery } from "../../utils";
import { db } from "../../../config/firebase";
import CalendarDate from "../../../models/calendars/CalendarDate";

const QUERY_KEY = "calendarDates";

/**
 * Hook to fetch calendar date items from a calendar's "dates" subcollection.
 * There is no pagination or total count returned. No limit on retrieved items.
 */

export default function useCalendarDates(calendarId = "") {
  return useQuery({
    queryKey: [QUERY_KEY, calendarId],
    enabled: !!calendarId,
    staleTime: 60_000,
    cacheTime: 5 * 60_000,
    queryFn: async () => {
      const colRef = collection(db, "calendars", String(calendarId), "dates");
      const datesQuery = query(colRef, orderBy("date", "asc"));

      const snapshot = await getDocs(datesQuery);
      const items = snapshot.docs.map((doc) => new CalendarDate({ ...doc.data() }));

      return items;
    },
    onError: (err) => {
      console.error("Failed to fetch calendar dates.", err);
    },
  });
}

/**
 * Hook that returns a function to invalidate the query and cause it to refetch.
 */
export const useInvalidateCalendarDates = () => {
  const queryClient = useQueryClient();
  return (calendarId) => invalidateQuery({ queryClient, queryKey: QUERY_KEY, id: calendarId });
};
